import { useEffect, useRef } from 'react';

const isTyping = () => {
  const el = document.activeElement;
  return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
};

// Keyboard: space/k play-pause · ←/→ seek 10s · j/l seek 10s · m mute · f fullscreen · n/p next/prev · esc close
// When `enabled` is false only Escape is handled (so overlays can still be dismissed).
export const useTheatreKeys = (handlers, enabled = true) => {
  const ref = useRef(handlers);
  ref.current = handlers;
  useEffect(() => {
    const onKey = (e) => {
      if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      const h = ref.current;
      if (!enabled) { if (e.key === 'Escape' && h.escape) { e.preventDefault(); h.escape(); } return; }
      const map = {
        ' ': h.togglePlay, k: h.togglePlay, ArrowLeft: () => h.seekBy(-10), ArrowRight: () => h.seekBy(10),
        j: () => h.seekBy(-10), l: () => h.seekBy(10), m: h.toggleMute, f: h.toggleFullscreen,
        n: h.next, p: h.prev, Escape: h.escape,
      };
      const fn = map[e.key];
      if (fn) { e.preventDefault(); fn(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
};

// Tap once → toggle play · double-tap left/right third → seek ∓/±10s · horizontal swipe → prev/next
export const useSurfaceGestures = ({ onToggle, onSeekBy, onNext, onPrev, onActivity }) => {
  const last = useRef({ t: 0, x: 0 });
  const timer = useRef(null);
  const touch = useRef(null);

  const onClick = (e) => {
    onActivity?.();
    if (e.target !== e.currentTarget && e.target.tagName !== 'VIDEO') return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const now = Date.now();
    if (now - last.current.t < 280) {
      clearTimeout(timer.current);
      last.current = { t: 0, x };
      if (x < 0.35) onSeekBy?.(-10);
      else if (x > 0.65) onSeekBy?.(10);
      else onToggle?.();
      return;
    }
    last.current = { t: now, x };
    timer.current = setTimeout(() => { onToggle?.(); }, 290);
  };

  const onTouchStart = (e) => {
    onActivity?.();
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = touch.current; touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x; const dy = t.clientY - s.y;
    if (Math.abs(dx) > 70 && Math.abs(dy) < 50 && Date.now() - s.at < 600) {
      e.preventDefault();
      clearTimeout(timer.current); last.current = { t: 0, x: 0 };
      if (dx < 0) onNext?.(); else onPrev?.();
    }
  };

  useEffect(() => () => clearTimeout(timer.current), []);
  return { onClick, onTouchStart, onTouchEnd };
};
